import {initialCenter, scrollWheelZoom, style, zoom, zoomDelta, zoomSnap, defaultColor, featureStyle} from "./config";
import {GeoJSON, MapContainer, TileLayer, useMap} from 'react-leaflet';
import L, {Layer, LeafletMouseEvent, PointTuple} from "leaflet";
import {labels, shapes} from '../../data/shared/xyz.json';
import * as geojson from 'geojson';
import {FeatureCollection} from 'geojson';
import {useEffect, useRef, useState} from "react";
import {Legend} from "./Legend/Legend";
import './MapChart.scss';
import {getColor} from './utils';
import {includesCaseInsesitive} from "../utils";


const FitBounds = ({bounds}: any) => {
    const map = useMap();

    const resize = () => {
        if (bounds.current) {
            map.fitBounds(bounds.current.getBounds(), {
                padding: [0, 0],
                animate: true,
            });
        }
    }

    map.on('resize', () => resize());

    useEffect(() => resize(), [bounds]);

    return (<></>);
};

const shapesData: FeatureCollection = shapes as FeatureCollection;
const labelsData: FeatureCollection = labels as FeatureCollection;

type MapChartProps = {
    data: Array<any>,
    groupBy: string;
    value: string;
    filters: Array<any>;
    activeFilters: Array<any>;
    valueFormat?: string;
    featureClick: (key: string, valuse: Array<string>) => void;
}

const MapChart = ({data, groupBy, featureClick, value, activeFilters, filters, valueFormat}: MapChartProps) => {

    const [clickedItems, setClickedItems] = useState<Array<string>>([]);
    const [mapData, setMapData] = useState<Array<any> | null>( null);
    const [max, setMax] = useState<number>( 0);
    const [calculateWorker, setCalculateWorker] = useState<any>();

    useEffect(() => {
        setCalculateWorker(new Worker(window.location.origin  + '/workers/map-worker.js'));
    }, []);


    useEffect(() => {
        if (!calculateWorker) return;
        calculateWorker.onmessage = ($event: MessageEvent) => {
            if ($event && $event.data) {
                setMax($event.data.newMax);
                setMapData($event.data.newMapData);
            }
        };
    }, [calculateWorker]);

    useEffect(() => {
        if (!calculateWorker) return;
        calculateWorker.postMessage({data,groupBy, value, valueFormat});
    }, [data]);

    useEffect(() => {
        featureClick(groupBy, clickedItems)
    }, [clickedItems]);

    useEffect(()=>{
        if (shapesRef.current) {
            // @ts-ignore
            shapesRef.current.eachLayer((layer) => updateStyle(layer, mapData, max));
        }
    }, [mapData]);

    useEffect(() => {
        if (activeFilters.length !== 0) {
            const organizationIndex = activeFilters.findIndex((value: any) => value['key'].toLowerCase() === 'organization');

            if (organizationIndex !== -1) {
                const {active} = activeFilters[organizationIndex];
                setClickedItems(active);
            } else {
                if (clickedItems.length !== 0) {
                    setClickedItems([]);
                }
            }
        } else {
            if (clickedItems.length !== 0) {
                setClickedItems([]);
            }
        }

        if (shapesRef.current) {
            const organizationIndex = filters.findIndex((value: any) => value['key'].toLowerCase() === 'organization');

            // @ts-ignore
            shapesRef.current.eachLayer((layer) => {
                const {feature} = layer;
                const {nazwa} = feature.properties;

                if (!(organizationIndex !== -1 && includesCaseInsesitive(filters[organizationIndex].data, nazwa))) return;

                if (!activeFilters.length) {
                    layer.setStyle({
                        color: '#fff',
                        weight: .8,
                        className: '',
                    });
                }

                activeFilters.filter(item => item.key === 'organization').forEach(({active}: any) => {
                    const isClicked = layer.options.className === 'clicked';

                    if (includesCaseInsesitive(active, nazwa) && (!isClicked)) {
                        layer.setStyle({
                            color: 'yellow',
                            weight: 5,
                            className: 'clicked',
                        });
                    } else if (!includesCaseInsesitive(active, nazwa) && (layer.options.className === 'clicked')) {
                        layer.setStyle({
                            color: '#fff',
                            weight: .8,
                            className: '',
                        });
                    }
                })
            });
        }
    }, [activeFilters]);


    const updateStyle = (layer: any, data: any, max: number) => {
        layer.setStyle({
            fillColor: defaultColor
        });

        const {feature} = layer;
        const organizationIndex = filters.findIndex((value: any) => value['key'].toLowerCase() === 'organization');

        if (organizationIndex !== -1 && feature.properties.nazwa && includesCaseInsesitive(filters[organizationIndex].data, feature.properties.nazwa)) {
            const {nazwa} = feature.properties;
            // @ts-ignore
            const featureExistInData = mapData.filter((item: any) => item[0].toLowerCase() === nazwa.toLowerCase());
            if (featureExistInData.length) {
                layer.setStyle({
                    fillColor: getColor(featureExistInData[0][1], max)
                });

                layer.on({
                    mouseover: (event: LeafletMouseEvent) => {
                        event.target.setStyle(featureStyle.mouseOver);
                    },
                    mouseout: (event: LeafletMouseEvent) => {
                        if (layer.options.className === 'clicked') return;
                        event.target.setStyle(featureStyle.mouseOut);
                    },
                    click: (event: LeafletMouseEvent) => {
                        // @ts-ignore
                        if (layer.options.className !== 'clicked') {
                            event.target.setStyle(featureStyle.clicked);
                            setClickedItems(previousValues => [...previousValues, featureExistInData[0][0]]);
                        } else {
                            event.target.setStyle(featureStyle.normal);
                            setClickedItems(previousValues => previousValues.filter(item => item !== featureExistInData[0][0]));
                        }
                    },
                })

            }
        }
    }

    const onEachMarker = (feature: geojson.Feature<geojson.GeometryObject>, layer: Layer) => {
        if (feature.properties && feature.properties.nazwa) {
            // @ts-ignore
            layer.options.icon = new L.DivIcon({
                html: `${feature.properties.nazwa}`,
                className: `marker-label ${feature.properties.nazwa}`,
                iconAnchor: [40, 10],
                iconSize: [80, 20] as PointTuple
            });
        }
    }

    const shapesRef = useRef(null);

    return (
        <div className="map-container chart">
            <MapContainer id="mapId"
                          center={initialCenter}
                          zoom={zoom}
                          scrollWheelZoom={scrollWheelZoom}
                          zoomSnap={zoomSnap}
                          zoomDelta={zoomDelta}>
                <TileLayer
                    attribution='&copy; <a href="http://osm.org/copyright">Canvas/World_Light_Gray_Base</a> contributors'
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}"/>
                <GeoJSON data={labelsData} onEachFeature={onEachMarker}/>
                <GeoJSON data={shapesData} style={style} ref={shapesRef}/>
                <Legend max={max}/>
                <FitBounds bounds={shapesRef}/>
            </MapContainer>
            <div className={'map-info'}>
                <p>Kliknij by zaznaczyć gminę</p>
            </div>
        </div>
    )
}

export {MapChart}